import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { XCircleIcon } from "@phosphor-icons/react";
import { useNavigate, useLocation } from "react-router-dom";

import { useLeadsOptedInTasSlice } from "./slice/index";
import {
  leadsOptedInListSelector,
  createLeadsOptedInSelector,
  updateLeadsOptedInSelector,
  deleteLeadsOptedInSelector,
  reOptInSelector
} from "./slice/selector";

import LoadingSpinner from "components/PageLoader/Spinner";
import RadioLabelComponent from "components/Toggles/RadioLabelComponent";
import {
  FooterWrapper,
  OptedField,
  OptedInLeadsStyles,
  OptedLeadChip,
  OptedSearchInput
} from "./styled";
import Icon from "components/Icon/Icon";
import Button from "components/Buttons/index";
import Textarea from "components/Input/Textarea";
import { formatTimeZone } from "utils/helper";

const OptedInLeads = ({ loading, data, size = "M" }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const prevSearch = location.state?.prevSearch || "";

  const { actions } = useLeadsOptedInTasSlice();

  const { records } = useSelector(leadsOptedInListSelector);
  const createState = useSelector(createLeadsOptedInSelector);
  const updateState = useSelector(updateLeadsOptedInSelector);
  const deleteState = useSelector(deleteLeadsOptedInSelector);
  const reOptInState = useSelector(reOptInSelector);

  const agencyId = data?.company_id;

  const [mode, setMode] = useState(null);
  const [existingRecord, setExistingRecord] = useState(null);

  const [formData, setFormData] = useState({
    to_emails: [],
    send_instant_leads: 1,
    send_daily_leads: 1
  });

  const [deleteReason, setDeleteReason] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [errors, setErrors] = useState({ email: "", reason: "" });

  const setError = (field, msg) =>
    setErrors((prev) => ({ ...prev, [field]: msg }));

  const clearError = (field) => setErrors((prev) => ({ ...prev, [field]: "" }));

  const MAX_EMAILS = 5;

  const parseEmails = (emails) => {
    if (!emails) return [];
    return emails
      .split(",")
      .map((e) => e.trim())
      .filter((e) => e);
  };

  // ─── Fetch ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!agencyId) return;
    dispatch(
      actions.fetchLeadsOptedInList({
        page: 1,
        limit: 10,
        company_id: agencyId
      })
    );
  }, [agencyId]);

  // ─── Populate form ────────────────────────────────────────────────
  useEffect(() => {
    if (!agencyId) return;
    const record = records?.find((r) => r.company_id === Number(agencyId));

    if (record) {
      setExistingRecord(record);
      setFormData({
        to_emails: parseEmails(record.to_emails),
        send_instant_leads: record.send_instant_leads ?? 1,
        send_daily_leads: record.send_daily_leads ?? 1
      });
      setDeleteReason(record.reason || "");
      setMode(record.deleted_at ? "optout" : "optin");
    } else {
      setExistingRecord(null);
      setDeleteReason("");
      setMode(null);
    }
  }, [records, agencyId]);

  // ─── Success handlers ─────────────────────────────────────────────
  useEffect(() => {
    if (createState.success) {
      dispatch(actions.resetCreateLeadsOptedIn());
      // navigate("/talent-agencies");
      navigate(`/talent-agencies${prevSearch}`);
    }
  }, [createState.success]);

  useEffect(() => {
    if (updateState.success) {
      dispatch(actions.resetUpdateLeadsOptedIn());
      navigate(`/talent-agencies${prevSearch}`);
    }
  }, [updateState.success]);

  useEffect(() => {
    if (deleteState.success) {
      dispatch(actions.resetDeleteLeadsOptedIn());
      navigate(`/talent-agencies${prevSearch}`);
    }
  }, [deleteState.success]);

  useEffect(() => {
    if (reOptInState.success) {
      dispatch(actions.resetReOptIn());
      navigate(`/talent-agencies${prevSearch}`);
    }
  }, [reOptInState.success]);

  // ─── Toggle ───────────────────────────────────────────────────────
  const handleToggle = (selected) => {
    if (selected === mode) return;
    setMode(selected);
    setErrors({ email: "", reason: "" });
  };

  // ─── Save ─────────────────────────────────────────────────────────
  const handleSave = () => {
    const finalEmails = [...formData.to_emails];

    if (emailInput.trim()) {
      if (!isValidEmail(emailInput.trim())) {
        setError("email", "Invalid email format");
        return;
      }
      if (!finalEmails.includes(emailInput.trim())) {
        finalEmails.push(emailInput.trim());
      }
      setEmailInput("");
    }

    const payload = {
      ...formData,
      to_emails: finalEmails.join(",")
    };

    if (mode === "optin") {
      if (existingRecord) {
        if (existingRecord.deleted_at) {
          dispatch(actions.reOptIn({ id: agencyId }));
        } else {
          dispatch(actions.updateLeadsOptedIn({ id: agencyId, ...payload }));
        }
      } else {
        dispatch(
          actions.createLeadsOptedIn({ company_id: agencyId, ...payload })
        );
      }
    }

    if (mode === "optout") {
      dispatch(
        actions.deleteLeadsOptedIn({ id: agencyId, reason: deleteReason })
      );
    }
  };

  // ─── Email helpers ────────────────────────────────────────────────
  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleEmailAdd = (value) => {
    const rawEmails = value
      .split(/[,\s]+/)
      .map((e) => e.trim())
      .filter(Boolean);
    const newEmails = [];
    rawEmails.forEach((email) => {
      if (
        isValidEmail(email) &&
        !formData.to_emails.includes(email) &&
        formData.to_emails.length + newEmails.length < MAX_EMAILS
      ) {
        newEmails.push(email);
      }
    });
    if (newEmails.length > 0) {
      setFormData((prev) => ({
        ...prev,
        to_emails: [...prev.to_emails, ...newEmails]
      }));
      clearError("email");
    }
  };

  const handleEmailKeyDown = (e) => {
    if (e.key === "Enter" || e.key === "," || e.key === " ") {
      e.preventDefault();
      if (!emailInput.trim()) {
        setError("email", "Email cannot be empty");
        return;
      }
      handleEmailAdd(emailInput);
      setEmailInput("");
      return;
    }
    if (e.key === "Backspace" && !emailInput && formData.to_emails.length > 0) {
      setFormData((prev) => ({
        ...prev,
        to_emails: prev.to_emails.slice(0, -1)
      }));
      clearError("email");
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    handleEmailAdd(e.clipboardData.getData("text"));
  };

  const removeEmail = (email) => {
    setFormData((prev) => ({
      ...prev,
      to_emails: prev.to_emails.filter((e) => e !== email)
    }));
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setEmailInput(value);
    if (!value.trim()) {
      clearError("email");
      return;
    }
    const parts = value.split(/[,\s]+/).filter(Boolean);
    const lastEmail = parts[parts.length - 1];
    if (!isValidEmail(lastEmail)) {
      setError("email", "Invalid email format");
      return;
    }
    if (formData.to_emails.includes(lastEmail)) {
      setError("email", "Duplicate email not allowed");
      return;
    }
    if (formData.to_emails.length >= MAX_EMAILS) {
      setError("email", `Maximum ${MAX_EMAILS} emails allowed`);
      return;
    }
    clearError("email");
  };

  const isSaveLoading =
    createState.loading ||
    updateState.loading ||
    deleteState.loading ||
    reOptInState.loading;

  const isOptOut = mode === "optout";
  const isOptIn = mode === "optin";

  return (
    <>
      <OptedInLeadsStyles>
        <h3>Opted-In Companies</h3>

        {/* ── Toggles ── */}
        <div className="choose-opt-leads">
          <RadioLabelComponent
            labelHeader="Yes - Opt-in Company"
            checked={isOptIn}
            onChange={() => handleToggle("optin")}
          />
          <RadioLabelComponent
            labelHeader="No - Opt-out Company"
            checked={isOptOut}
            onChange={() => handleToggle("optout")}
          />
        </div>

        {isOptIn && (
          <div className="data-wrapper">
            {/* ── Left column ── */}
            <div className="side-wrapper">
              <div className="side-wrapper-list">
                <span className="heading">Company Id</span>
                <p>{agencyId}</p>
              </div>

              {/* ── To Emails — always visible, disabled when optout ── */}
              <div className="side-wrapper-list">
                <label>To Emails</label>
                <OptedField
                  className={`${errors.email ? "error" : ""} ${isOptOut ? "disabled" : ""}`}
                >
                  <div className="field-wrapper">
                    {formData.to_emails.map((email, index) => (
                      <OptedLeadChip key={index}>
                        <h5>{email}</h5>
                        {isOptIn && (
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              removeEmail(email);
                            }}
                          >
                            <Icon
                              component={XCircleIcon}
                              size={size === "S" ? 10 : 12}
                            />
                          </span>
                        )}
                      </OptedLeadChip>
                    ))}

                    {/* input only when optin */}
                    {isOptIn && (
                      <OptedSearchInput
                        type="text"
                        size={size}
                        value={emailInput}
                        placeholder="Enter email"
                        onChange={handleInputChange}
                        onKeyDown={handleEmailKeyDown}
                        onPaste={handlePaste}
                      />
                    )}

                    {loading && <LoadingSpinner size={12} />}
                  </div>
                </OptedField>
                {errors.email && (
                  <span className="error-text">{errors.email}</span>
                )}
              </div>
            </div>

            <div className="side-wrapper">
              <div className="side-wrapper-list">
                <label className="toggle-label">Send Daily Leads</label>
                <RadioLabelComponent
                  labelHeader="Yes"
                  checked={formData.send_daily_leads === 1}
                  state={isOptOut ? "disabled" : "default"}
                  onChange={() =>
                    !isOptOut && handleChange("send_daily_leads", 1)
                  }
                />
                <RadioLabelComponent
                  labelHeader="No"
                  checked={formData.send_daily_leads === 0}
                  state={isOptOut ? "disabled" : "default"}
                  onChange={() =>
                    !isOptOut && handleChange("send_daily_leads", 0)
                  }
                />
              </div>

              <div className="side-wrapper-list">
                <label className="toggle-label">Send Instant Leads</label>
                <RadioLabelComponent
                  labelHeader="Yes"
                  checked={formData.send_instant_leads === 1}
                  state={isOptOut ? "disabled" : "default"}
                  onChange={() =>
                    !isOptOut && handleChange("send_instant_leads", 1)
                  }
                />
                <RadioLabelComponent
                  labelHeader="No"
                  checked={formData.send_instant_leads === 0}
                  state={isOptOut ? "disabled" : "default"}
                  onChange={() =>
                    !isOptOut && handleChange("send_instant_leads", 0)
                  }
                />
              </div>
            </div>

            {/* ── Right column ── */}
            <div className="side-wrapper">
              <div className="side-wrapper-list">
                <span className="heading">Created at</span>
                <p>{formatTimeZone(existingRecord?.created_at) || "-"}</p>
              </div>
            </div>
          </div>
        )}

        {isOptOut && (
          <div className="data-wrapper">
            <div className="side-wrapper">
              <div className="side-wrapper-list">
                <Textarea
                  id="retool_comments"
                  label="Reason"
                  required
                  placeholder="Add Reason here"
                  rows={3}
                  value={deleteReason}
                  onChange={(e) => {
                    setDeleteReason(e.target.value);
                    clearError("reason");
                  }}
                />
                {errors.reason && <p className="error-text">{errors.reason}</p>}
              </div>
            </div>
          </div>
        )}
      </OptedInLeadsStyles>

      {mode && (
        <FooterWrapper>
          <Button variant="secondary" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button
            size="M"
            variant="primary"
            onClick={handleSave}
            loading={isSaveLoading}
          >
            Save Changes
          </Button>
        </FooterWrapper>
      )}
    </>
  );
};

export default OptedInLeads;
